/*
 * Geometrie der freien Anordnung (docs/hmi-panel-ausbau.md §2): Rechtecke in
 * Pixeln der Entwurfsflaeche, Einrasten mit Hilfslinien, Griffe zum Ziehen
 * der Groesse, Ausrichten, Verteilen, Ebenen.
 *
 * Reines Modul ohne Laufzeit-Importe (nur `import type`), aus demselben Grund
 * wie `editorModel.ts`: der Testlaeufer (`node --experimental-strip-types`)
 * laedt es direkt, siehe tests/freeModel.test.ts. Alles hier rechnet in
 * Entwurfspixeln; die Umrechnung aus Bildschirmpixeln (geteilt durch den
 * Massstab der Buehne) macht die Flaeche, bevor sie hier hereinruft.
 *
 * Ganze Pixel. Die Panels zeichnen 1:1, und eine Kante bei 120,4 px wird dort
 * unscharf. Deshalb runden alle Funktionen, die ein Rechteck herausgeben.
 */

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface FrameSize {
  width: number;
  height: number;
}

/** Rasterweite fuer das Einrasten ohne Nachbarn. */
export const SNAP_GRID_PX = 8;
/** Abstand in Bildschirmpixeln, ab dem eine Kante einrastet. Die Flaeche teilt
 *  ihn durch den Massstab, damit er sich bei jeder Zoomstufe gleich anfuehlt. */
export const SNAP_THRESHOLD_SCREEN_PX = 6;
/** Kleinste Kantenlaenge eines Bausteins. Darunter ist er nicht mehr zu
 *  greifen, auch nicht im Editor. */
export const MIN_ITEM_PX = 8;
/** Versatz beim Duplizieren: sichtbar daneben, nicht deckungsgleich. */
export const DUPLICATE_OFFSET_PX = 16;

/** Eine Hilfslinie: `v` ist senkrecht bei x = pos, `h` waagrecht bei y = pos.
 *  `start` und `end` sind die Ausdehnung entlang der Linie. */
export interface Guide {
  axis: 'v' | 'h';
  pos: number;
  start: number;
  end: number;
}

export type Handle = 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw';
export const HANDLES: readonly Handle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

export function roundRect(r: Rect): Rect {
  return { x: Math.round(r.x), y: Math.round(r.y), w: Math.round(r.w), h: Math.round(r.h) };
}

/**
 * Rechteck in den Rahmen zwingen: Groesse zuerst (nie groesser als der
 * Rahmen, nie kleiner als {@link MIN_ITEM_PX}), dann die Lage.
 */
export function clampRect(r: Rect, frame: FrameSize, minSize = MIN_ITEM_PX): Rect {
  const w = Math.max(minSize, Math.min(finite(r.w, 100), frame.width));
  const h = Math.max(minSize, Math.min(finite(r.h, 100), frame.height));
  const x = Math.max(0, Math.min(finite(r.x, 0), frame.width - w));
  const y = Math.max(0, Math.min(finite(r.y, 0), frame.height - h));
  return roundRect({ x, y, w, h });
}

function finite(v: number, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

export function rectsIntersect(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/** Ist `inner` ganz in `frame`? */
export function insideFrame(r: Rect, frame: FrameSize): boolean {
  return r.x >= 0 && r.y >= 0 && r.x + r.w <= frame.width && r.y + r.h <= frame.height;
}

/** Umschliessendes Rechteck mehrerer Rechtecke, oder null bei keinem. */
export function boundsOf(rects: readonly Rect[]): Rect | null {
  if (rects.length === 0) return null;
  let x1 = Infinity;
  let y1 = Infinity;
  let x2 = -Infinity;
  let y2 = -Infinity;
  for (const r of rects) {
    x1 = Math.min(x1, r.x);
    y1 = Math.min(y1, r.y);
    x2 = Math.max(x2, r.x + r.w);
    y2 = Math.max(y2, r.y + r.h);
  }
  return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
}

/** Rechteck aus zwei Eckpunkten (Gummiband), egal in welche Richtung gezogen. */
export function rectFromPoints(a: { x: number; y: number }, b: { x: number; y: number }): Rect {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    w: Math.abs(a.x - b.x),
    h: Math.abs(a.y - b.y),
  };
}

// ------------------------------------------------------------- Einrasten

interface AxisSnap {
  delta: number;
  /** Die Linien der Ziele, an denen eingerastet wurde. */
  lines: number[];
}

/**
 * Bestes Einrasten auf einer Achse: die kleinste Verschiebung, die eine der
 * eigenen Linien (`own`) auf eine Ziellinie (`targets`) legt, sofern sie nicht
 * groesser als `threshold` ist.
 */
function snapAxis(own: readonly number[], targets: readonly number[], threshold: number): AxisSnap | null {
  let best: number | null = null;
  for (const a of own) {
    for (const t of targets) {
      const d = t - a;
      if (Math.abs(d) <= threshold && (best === null || Math.abs(d) < Math.abs(best))) best = d;
    }
  }
  if (best === null) return null;
  const delta = best;
  const lines = targets.filter((t) => own.some((a) => Math.abs(a + delta - t) < 0.5));
  return { delta, lines: Array.from(new Set(lines)) };
}

function xLines(r: Rect): number[] {
  return [r.x, r.x + r.w / 2, r.x + r.w];
}
function yLines(r: Rect): number[] {
  return [r.y, r.y + r.h / 2, r.y + r.h];
}

export interface SnapOptions {
  /** Abstand in Entwurfspixeln. */
  threshold: number;
  /** Rasterweite, wenn keine Kante in der Naehe ist; 0 schaltet ab. */
  grid: number;
  /** An Kanten und Mitten anderer Bausteine und des Rahmens einrasten. */
  objects: boolean;
}

export interface SnapResult {
  rect: Rect;
  guides: Guide[];
}

function frameRect(frame: FrameSize): Rect {
  return { x: 0, y: 0, w: frame.width, h: frame.height };
}

/**
 * Hilfslinien fuer ein eingerastetes Rechteck: zu jeder Ziellinie die
 * Strecke, die das bewegte Rechteck und alle Ziele mit dieser Linie
 * ueberspannt. So sieht der Autor, WORAN es eingerastet ist.
 */
function guidesFor(rect: Rect, axis: 'v' | 'h', lines: readonly number[], targets: readonly Rect[]): Guide[] {
  const out: Guide[] = [];
  for (const pos of lines) {
    let start = axis === 'v' ? rect.y : rect.x;
    let end = axis === 'v' ? rect.y + rect.h : rect.x + rect.w;
    for (const t of targets) {
      const own = axis === 'v' ? xLines(t) : yLines(t);
      if (!own.some((l) => Math.abs(l - pos) < 0.5)) continue;
      start = Math.min(start, axis === 'v' ? t.y : t.x);
      end = Math.max(end, axis === 'v' ? t.y + t.h : t.x + t.w);
    }
    out.push({ axis, pos, start, end });
  }
  return out;
}

/**
 * Ein verschobenes Rechteck einrasten.
 *
 * Erst an Kanten und Mitten der anderen Bausteine und des Rahmens; wo auf
 * einer Achse nichts in der Naehe ist, auf das Raster. Die Groesse bleibt.
 */
export function snapMove(
  rect: Rect,
  others: readonly Rect[],
  frame: FrameSize,
  opts: SnapOptions,
): SnapResult {
  const targets = opts.objects ? [...others, frameRect(frame)] : [];
  const tx = targets.flatMap(xLines);
  const ty = targets.flatMap(yLines);

  const sx = opts.objects ? snapAxis(xLines(rect), tx, opts.threshold) : null;
  const sy = opts.objects ? snapAxis(yLines(rect), ty, opts.threshold) : null;

  let x = rect.x;
  let y = rect.y;
  if (sx) x += sx.delta;
  else if (opts.grid > 0) x = Math.round(x / opts.grid) * opts.grid;
  if (sy) y += sy.delta;
  else if (opts.grid > 0) y = Math.round(y / opts.grid) * opts.grid;

  const moved = roundRect({ x, y, w: rect.w, h: rect.h });
  const guides = [
    ...(sx ? guidesFor(moved, 'v', sx.lines, targets) : []),
    ...(sy ? guidesFor(moved, 'h', sy.lines, targets) : []),
  ];
  return { rect: moved, guides };
}

// ------------------------------------------------------------ Groesse

export interface ResizeOptions {
  minSize?: number;
  /** Seitenverhaeltnis halten (Umschalttaste an einem Eckgriff). */
  keepAspect?: boolean;
}

/**
 * Rechteck an einem Griff um (dx, dy) Entwurfspixel ziehen.
 *
 * Die gegenueberliegende Kante bleibt stehen. Wird ein Rechteck kleiner als
 * die Mindestgroesse gezogen, bleibt es bei der Mindestgroesse stehen, statt
 * umzuklappen: ein Baustein, der beim Ziehen ueber die eigene Kante hinweg
 * spiegelt, ist schwer wieder einzufangen.
 */
export function resizeRect(start: Rect, handle: Handle, dx: number, dy: number, opts: ResizeOptions = {}): Rect {
  const min = opts.minSize ?? MIN_ITEM_PX;
  const west = handle.includes('w');
  const east = handle.includes('e');
  const north = handle.includes('n');
  const south = handle.includes('s');

  let left = start.x;
  let right = start.x + start.w;
  let top = start.y;
  let bottom = start.y + start.h;

  if (west) left = Math.min(left + dx, right - min);
  if (east) right = Math.max(right + dx, left + min);
  if (north) top = Math.min(top + dy, bottom - min);
  if (south) bottom = Math.max(bottom + dy, top + min);

  if (opts.keepAspect && (west || east) && (north || south) && start.h > 0) {
    const ratio = start.w / start.h;
    let w = right - left;
    let h = bottom - top;
    if (Math.abs(w / start.w) >= Math.abs(h / start.h)) h = w / ratio;
    else w = h * ratio;
    w = Math.max(min, w);
    h = Math.max(min, h);
    if (west) left = right - w;
    else right = left + w;
    if (north) top = bottom - h;
    else bottom = top + h;
  }

  return { x: left, y: top, w: right - left, h: bottom - top };
}

/**
 * Die bewegten Kanten eines in der Groesse gezogenen Rechtecks einrasten.
 * Nur die Kanten, die der Griff bewegt; die feste Kante bleibt, wo sie ist.
 */
export function snapResize(
  rect: Rect,
  handle: Handle,
  others: readonly Rect[],
  frame: FrameSize,
  opts: SnapOptions & { minSize?: number },
): SnapResult {
  const min = opts.minSize ?? MIN_ITEM_PX;
  const targets = opts.objects ? [...others, frameRect(frame)] : [];
  const tx = targets.flatMap(xLines);
  const ty = targets.flatMap(yLines);

  let left = rect.x;
  let right = rect.x + rect.w;
  let top = rect.y;
  let bottom = rect.y + rect.h;
  const guides: Guide[] = [];

  const snapEdge = (value: number, lines: readonly number[]): { value: number; line: number | null } => {
    const s = opts.objects ? snapAxis([value], lines, opts.threshold) : null;
    if (s) return { value: value + s.delta, line: value + s.delta };
    if (opts.grid > 0) return { value: Math.round(value / opts.grid) * opts.grid, line: null };
    return { value, line: null };
  };

  const vLines: number[] = [];
  const hLines: number[] = [];
  if (handle.includes('w')) {
    const s = snapEdge(left, tx);
    left = Math.min(s.value, right - min);
    if (s.line !== null) vLines.push(s.line);
  }
  if (handle.includes('e')) {
    const s = snapEdge(right, tx);
    right = Math.max(s.value, left + min);
    if (s.line !== null) vLines.push(s.line);
  }
  if (handle.includes('n')) {
    const s = snapEdge(top, ty);
    top = Math.min(s.value, bottom - min);
    if (s.line !== null) hLines.push(s.line);
  }
  if (handle.includes('s')) {
    const s = snapEdge(bottom, ty);
    bottom = Math.max(s.value, top + min);
    if (s.line !== null) hLines.push(s.line);
  }

  const out = roundRect({ x: left, y: top, w: right - left, h: bottom - top });
  guides.push(...guidesFor(out, 'v', vLines, targets), ...guidesFor(out, 'h', hLines, targets));
  return { rect: out, guides };
}

// ---------------------------------------------- Ausrichten und Verteilen

export type FreeAlign = 'left' | 'hcenter' | 'right' | 'top' | 'vcenter' | 'bottom';

/**
 * Rechtecke ausrichten.
 *
 * Bei mehreren ist der Bezug das ZUERST gewaehlte, wie im Raster (siehe
 * `alignPlacements` in editorModel.ts): der Autor waehlt in einer
 * Reihenfolge, und seine erste Wahl ist die, die er schon fuer richtig haelt.
 * Bei einem einzelnen ist der Bezug der Rahmen; so steht ein Knopf mit einem
 * Klick mittig auf dem Panel.
 */
export function alignRects(
  rects: Readonly<Record<string, Rect>>,
  ids: readonly string[],
  mode: FreeAlign,
  frame: FrameSize,
): Record<string, Rect> {
  const out: Record<string, Rect> = { ...rects };
  const present = ids.filter((id) => out[id]);
  if (present.length === 0) return out;
  const anchor = present.length === 1 ? frameRect(frame) : out[present[0]];
  const movers = present.length === 1 ? present : present.slice(1);

  for (const id of movers) {
    const r = out[id];
    let { x, y } = r;
    switch (mode) {
      case 'left': x = anchor.x; break;
      case 'hcenter': x = anchor.x + anchor.w / 2 - r.w / 2; break;
      case 'right': x = anchor.x + anchor.w - r.w; break;
      case 'top': y = anchor.y; break;
      case 'vcenter': y = anchor.y + anchor.h / 2 - r.h / 2; break;
      case 'bottom': y = anchor.y + anchor.h - r.h; break;
    }
    out[id] = roundRect({ ...r, x, y });
  }
  return out;
}

/**
 * Gleiche Abstaende zwischen mindestens drei Rechtecken. Die beiden aeusseren
 * bleiben stehen, die inneren ruecken so, dass jede Luecke gleich gross ist.
 * Sortiert wird nach der Lage, nicht nach der Wahlreihenfolge: verteilt wird
 * im Bild, und dort zaehlt, was links von was liegt.
 */
export function distributeRects(
  rects: Readonly<Record<string, Rect>>,
  ids: readonly string[],
  axis: 'horizontal' | 'vertical',
): Record<string, Rect> {
  const out: Record<string, Rect> = { ...rects };
  const present = ids.filter((id) => out[id]);
  if (present.length < 3) return out;

  const horizontal = axis === 'horizontal';
  const sorted = [...present].sort((a, b) =>
    horizontal ? out[a].x - out[b].x || out[a].y - out[b].y : out[a].y - out[b].y || out[a].x - out[b].x);
  const first = out[sorted[0]];
  const last = out[sorted[sorted.length - 1]];
  const start = horizontal ? first.x : first.y;
  const end = horizontal ? last.x + last.w : last.y + last.h;
  const total = sorted.reduce((sum, id) => sum + (horizontal ? out[id].w : out[id].h), 0);
  const gap = (end - start - total) / (sorted.length - 1);

  let cursor = start;
  for (const id of sorted) {
    const r = out[id];
    out[id] = roundRect(horizontal ? { ...r, x: cursor } : { ...r, y: cursor });
    cursor += (horizontal ? r.w : r.h) + gap;
  }
  return out;
}

/** Alle auf Breite oder Hoehe des zuerst gewaehlten bringen. */
export function matchRectSize(
  rects: Readonly<Record<string, Rect>>,
  ids: readonly string[],
  dimension: 'width' | 'height',
): Record<string, Rect> {
  const out: Record<string, Rect> = { ...rects };
  const present = ids.filter((id) => out[id]);
  if (present.length < 2) return out;
  const anchor = out[present[0]];
  for (const id of present.slice(1)) {
    out[id] = dimension === 'width' ? { ...out[id], w: anchor.w } : { ...out[id], h: anchor.h };
  }
  return out;
}

// ---------------------------------------------------------------- Ebenen

export type ZOp = 'forward' | 'front' | 'backward' | 'back';

/**
 * Ebenen neu ordnen. `order` ist die Stapelfolge von unten nach oben, heraus
 * kommt die neue. Mehrere gewaehlte behalten ihre Reihenfolge untereinander.
 */
export function reorder(order: readonly string[], ids: readonly string[], op: ZOp): string[] {
  const chosen = new Set(ids);
  const out = [...order];
  switch (op) {
    case 'front':
      return [...out.filter((id) => !chosen.has(id)), ...out.filter((id) => chosen.has(id))];
    case 'back':
      return [...out.filter((id) => chosen.has(id)), ...out.filter((id) => !chosen.has(id))];
    case 'forward':
      for (let i = out.length - 2; i >= 0; i -= 1) {
        if (chosen.has(out[i]) && !chosen.has(out[i + 1])) [out[i], out[i + 1]] = [out[i + 1], out[i]];
      }
      return out;
    case 'backward':
      for (let i = 1; i < out.length; i += 1) {
        if (chosen.has(out[i]) && !chosen.has(out[i - 1])) [out[i], out[i - 1]] = [out[i - 1], out[i]];
      }
      return out;
  }
}

/** Stapelfolge (unten nach oben) aus z-Werten; bei Gleichstand zaehlt die
 *  Reihenfolge in `ids`, wie in FreeStage. */
export function stackOrder(ids: readonly string[], zOf: (id: string) => number | undefined): string[] {
  return ids
    .map((id, i) => ({ id, i, z: zOf(id) ?? 0 }))
    .sort((a, b) => a.z - b.z || a.i - b.i)
    .map((e) => e.id);
}

// ----------------------------------------------------------- Freier Platz

/**
 * Ein freier Platz fuer ein neues Rechteck der Groesse `size`: zeilenweise
 * von oben links im Abstand von 16 px die erste Stelle, an der es nichts
 * ueberdeckt. Findet sich keine, landet es versetzt in der Mitte, sichtbar
 * und als neu erkennbar, statt still unter einem anderen zu verschwinden.
 */
export function findFreeRect(
  taken: readonly Rect[],
  size: { w: number; h: number },
  frame: FrameSize,
  margin = 16,
): Rect {
  const w = Math.min(size.w, frame.width);
  const h = Math.min(size.h, frame.height);
  const step = 16;
  for (let y = margin; y + h <= frame.height; y += step) {
    for (let x = margin; x + w <= frame.width; x += step) {
      const candidate = { x, y, w, h };
      const padded = { x: x - 8, y: y - 8, w: w + 16, h: h + 16 };
      if (!taken.some((t) => rectsIntersect(padded, t))) return candidate;
    }
  }
  const cascade = (taken.length % 8) * DUPLICATE_OFFSET_PX;
  return clampRect({ x: (frame.width - w) / 2 + cascade, y: (frame.height - h) / 2 + cascade, w, h }, frame);
}
